import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FlaskConical, CheckCircle2, Home, MapPin, FileText, Download } from 'lucide-react';
import { mockLabTests } from '../../data/mockData';
export function LabTestsFlow() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedTests, setSelectedTests] = useState<string[]>([]);
  const [method, setMethod] = useState('home');
  const [trackingState, setTrackingState] = useState(0);
  const categories = ['Blood Tests', 'Urine Tests', 'Full Body Checkup', 'Infection Tests'];
  const toggleTest = (id: string) => {
    setSelectedTests(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  };
  const totalAmount = selectedTests.reduce((sum, id) => {
    const test = mockLabTests.find(t => t.id === id);
    return sum + (test ? test.price : 0);
  }, 0);
  const handlePayment = () => {
    setStep(6);
    const t1 = setTimeout(() => setTrackingState(1), 2000);
    const t2 = setTimeout(() => setTrackingState(2), 4000);
    const t3 = setTimeout(() => setTrackingState(3), 6000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  };
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-680">
      <div className="bg-white px-6 pt-12 pb-4 shadow-sm sticky top-0 z-10" data-id="element-681">
        <div className="flex items-center gap-3 mb-4" data-id="element-682">
          <button onClick={() => step > 1 && step < 6 ? setStep(step - 1) : navigate(-1)} className="p-2 -ml-2 hover:bg-slate-50 rounded-full" data-id="element-683">
            <ArrowLeft className="w-6 h-6 text-slate-700" data-id="element-684" />
          </button>
          <h1 className="text-xl font-bold text-slate-900" data-id="element-685">Lab Tests</h1>
        </div>
        {step < 6 && <div className="flex gap-2" data-id="element-686">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-teal-600' : 'bg-slate-200'}`} data-id="element-687" />)}
          </div>}
      </div>

      <div className="p-6 flex-1 overflow-y-auto" data-id="element-688">
        {step === 1 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-4" data-id="element-689">
            <h2 className="font-semibold text-slate-900 mb-2" data-id="element-690">Categories</h2>
            <div className="grid grid-cols-2 gap-3" data-id="element-691">
              {categories.map(cat => <button key={cat} onClick={() => setStep(2)} className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex flex-col items-center text-center hover:border-teal-200" data-id="element-692">
                  <FlaskConical className="w-8 h-8 text-teal-500 mb-2" data-id="element-693" />
                  <span className="font-medium text-slate-700 text-sm" data-id="element-694">
                    {cat}
                  </span>
                </button>)}
            </div>
          </motion.div>}

        {step === 2 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-4" data-id="element-695">
            <h2 className="font-semibold text-slate-900 mb-2" data-id="element-696">Select Tests</h2>
            <div className="space-y-3" data-id="element-697">
              {mockLabTests.map(test => <label key={test.id} className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer ${selectedTests.includes(test.id) ? 'border-teal-600 bg-teal-50' : 'border-slate-200 bg-white'}`} data-id="element-698">
                  <input type="checkbox" checked={selectedTests.includes(test.id)} onChange={() => toggleTest(test.id)} className="mt-1 w-5 h-5 rounded border-slate-300 text-teal-600 focus:ring-teal-600" data-id="element-699" />
                  <div className="flex-1" data-id="element-700">
                    <h3 className="font-bold text-slate-900" data-id="element-701">{test.name}</h3>
                    <p className="text-sm text-slate-500" data-id="element-702">{test.description}</p>
                    <p className="text-sm font-bold text-teal-600 mt-1" data-id="element-703">
                      ₦{test.price.toLocaleString()}
                    </p>
                  </div>
                </label>)}
            </div>
            <button onClick={() => setStep(3)} disabled={selectedTests.length === 0} className="w-full bg-teal-600 text-white py-3.5 rounded-xl font-semibold text-lg shadow-sm disabled:opacity-50 mt-6" data-id="element-704">
              Continue
            </button>
          </motion.div>}

        {step === 3 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-6" data-id="element-705">
            <h2 className="font-semibold text-slate-900 mb-2" data-id="element-706">
              Collection Method
            </h2>
            <div className="grid grid-cols-2 gap-3" data-id="element-707">
              <button onClick={() => setMethod('home')} className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 ${method === 'home' ? 'border-teal-600 bg-teal-50 text-teal-700' : 'border-slate-200 bg-white'}`} data-id="element-708">
                <Home className="w-6 h-6" data-id="element-709" />
                <span className="text-sm font-medium" data-id="element-710">Home Sample</span>
              </button>
              <button onClick={() => setMethod('lab')} className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 ${method === 'lab' ? 'border-teal-600 bg-teal-50 text-teal-700' : 'border-slate-200 bg-white'}`} data-id="element-711">
                <MapPin className="w-6 h-6" data-id="element-712" />
                <span className="text-sm font-medium" data-id="element-713">Visit Lab</span>
              </button>
            </div>
            {method === 'home' && <textarea className="w-full bg-white border border-slate-200 rounded-xl p-3 text-sm focus:border-teal-600 focus:ring-1 focus:ring-teal-600" rows={3} placeholder="Enter full address..." defaultValue="14 Admiralty Way, Lekki" data-id="element-714" />}
            <button onClick={() => setStep(4)} className="w-full bg-teal-600 text-white py-3.5 rounded-xl font-semibold text-lg shadow-sm" data-id="element-715">
              Continue
            </button>
          </motion.div>}

        {step === 4 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-6" data-id="element-716">
            <h2 className="font-semibold text-slate-900 mb-2" data-id="element-717">
              Select Date & Time
            </h2>
            <div className="bg-white p-4 rounded-xl border border-slate-200 flex justify-between items-center" data-id="element-718">
              <span className="font-medium" data-id="element-719">Today, Oct 12</span>
              <span className="text-teal-600 font-bold" data-id="element-720">09:00 AM</span>
            </div>
            <button onClick={() => setStep(5)} className="w-full bg-teal-600 text-white py-3.5 rounded-xl font-semibold text-lg shadow-sm" data-id="element-721">
              Continue to Summary
            </button>
          </motion.div>}

        {step === 5 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-6" data-id="element-722">
            <h2 className="text-xl font-bold text-slate-900" data-id="element-723">
              Payment Summary
            </h2>
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3" data-id="element-724">
              <div className="flex justify-between text-sm" data-id="element-725">
                <span className="text-slate-500" data-id="element-726">
                  {selectedTests.length} Tests
                </span>
                <span className="font-medium" data-id="element-727">
                  ₦{totalAmount.toLocaleString()}
                </span>
              </div>
              <div className="pt-3 border-t border-slate-100 flex justify-between font-bold text-lg" data-id="element-728">
                <span data-id="element-729">Total</span>
                <span className="text-teal-600" data-id="element-730">
                  ₦{totalAmount.toLocaleString()}
                </span>
              </div>
            </div>
            <button onClick={handlePayment} className="w-full bg-teal-600 text-white py-3.5 rounded-xl font-semibold text-lg shadow-sm" data-id="element-731">
              Pay Securely
            </button>
          </motion.div>}

        {step === 6 && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-732">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5" data-id="element-733">
              <h3 className="font-bold text-slate-900 mb-6 text-center" data-id="element-734">
                Test Status
              </h3>
              <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px before:h-full before:w-0.5 before:bg-slate-200" data-id="element-735">
                {['Booked', 'Sample Collected', 'Processing', 'Results Ready'].map((status, idx) => <div key={status} className="relative flex items-center gap-4" data-id="element-736">
                    <div className={`w-10 h-10 rounded-full border-4 border-white flex items-center justify-center z-10 transition-colors duration-500 ${trackingState >= idx ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-400'}`} data-id="element-737">
                      <CheckCircle2 className="w-5 h-5" data-id="element-738" />
                    </div>
                    <span className={`font-medium ${trackingState >= idx ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-739">
                      {status}
                    </span>
                  </div>)}
              </div>
            </div>
            {trackingState === 3 && <button onClick={() => setStep(7)} className="w-full bg-teal-600 text-white py-3.5 rounded-xl font-semibold text-lg shadow-sm animate-fade-in" data-id="element-740">
                View Results
              </button>}
          </motion.div>}

        {step === 7 && <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="flex flex-col items-center justify-center h-full text-center py-12" data-id="element-741">
            <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mb-6" data-id="element-742">
              <FileText className="w-10 h-10 text-teal-600" data-id="element-743" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-744">
              Results are ready!
            </h2>
            <p className="text-slate-500 mb-8" data-id="element-745">
              Your test results have been processed and are ready for review.
            </p>
            <div className="w-full space-y-3" data-id="element-746">
              <button className="w-full bg-teal-600 text-white py-3.5 rounded-xl font-semibold shadow-sm flex items-center justify-center gap-2" data-id="element-747">
                <FileText className="w-5 h-5" data-id="element-748" /> View Report
              </button>
              <button className="w-full bg-white text-teal-700 py-3.5 rounded-xl font-semibold border border-teal-200 shadow-sm flex items-center justify-center gap-2" data-id="element-749">
                <Download className="w-5 h-5" data-id="element-750" /> Download PDF
              </button>
            </div>
          </motion.div>}
      </div>
    </div>;
}